// 📁 utils/representanteUtils.js - CON FILTROS INHABILITADO COMPLETOS

// ========================================
// FUNCIONES DE BÚSQUEDA MEJORADAS
// ========================================

/**
 * Normaliza texto removiendo acentos y convirtiendo a minúsculas
 */
const normalizarTexto = (texto) => {
  if (!texto) return '';
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Calcula score de relevancia para ordenar resultados
 */
const calcularScore = (nombre, terminoBusqueda) => {
  const nombreNorm = normalizarTexto(nombre);
  const palabrasNombre = nombreNorm.split(/\s+/);
  const palabrasBusqueda = terminoBusqueda.split(/\s+/);
  
  let score = 0;
  
  // MÁXIMA PRIORIDAD: Nombre completo empieza con búsqueda
  if (nombreNorm.startsWith(terminoBusqueda)) {
    score += 10000;
  }
  
  // ALTA PRIORIDAD: Cada palabra de búsqueda al inicio de palabra del nombre
  palabrasBusqueda.forEach(pb => {
    const palabraCoincideAlInicio = palabrasNombre.find(pn => pn.startsWith(pb));
    if (palabraCoincideAlInicio) {
      // Si la palabra coincide exactamente, más puntos
      if (palabraCoincideAlInicio === pb) {
        score += 1000;
      } else {
        score += 500;
      }
    }
  });
  
  // Bonificación: Más palabras coinciden = mejor
  const numPalabrasCoinciden = palabrasBusqueda.filter(pb => 
    palabrasNombre.some(pn => pn.startsWith(pb))
  ).length;
  score += numPalabrasCoinciden * 200;
  
  // Penalización: Nombres muy largos
  score -= nombreNorm.length * 0.5;
  
  return score;
};

/**
 * Filtra representantes para búsqueda con lógica mejorada
 * ✅ INCLUYE FILTRO INHABILITADO
 */
export const filtrarRepresentantesParaBusqueda = (representantes, searchTerm) => {
  // Filtrar solo representantes con categorías activas Y que NO estén inhabilitados
  const representantesConCategorias = representantes.filter(
    (rep) => rep.categorias &&
    Array.isArray(rep.categorias) &&
    rep.categorias.some(cat => cat.Estado !== 'Inactivo') &&
    rep.estatus !== 'INHABILITADO' // ← FILTRO AGREGADO
  );

  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  const terminoBusqueda = normalizarTexto(searchTerm.trim());
  
  if (terminoBusqueda.length < 2) {
    return [];
  }

  const palabrasBusqueda = terminoBusqueda.split(/\s+/);

  // Filtrar
  const resultados = representantesConCategorias.filter(rep => {
    if (!rep) return false;

    const nombreCompleto = normalizarTexto(rep.nombre_representante || '');
    const dni = (rep.dni || '').toString().trim();
    const palabrasNombre = nombreCompleto.split(/\s+/);

    // Búsqueda por DNI
    if (/^\d+$/.test(terminoBusqueda)) {
      return dni.includes(terminoBusqueda);
    }

    // Todas las palabras de búsqueda deben estar en el nombre
    return palabrasBusqueda.every(palabraBusqueda => 
      palabrasNombre.some(palabraNombre => 
        palabraNombre.startsWith(palabraBusqueda)
      )
    );
  });

  // Ordenar por score de relevancia (mayor a menor)
  return resultados.sort((a, b) => {
    const scoreA = calcularScore(a.nombre_representante, terminoBusqueda);
    const scoreB = calcularScore(b.nombre_representante, terminoBusqueda);
    return scoreB - scoreA; // Mayor score primero
  });
};

// ========================================
// FUNCIONES EXISTENTES
// ========================================

// Conversión de números romanos - EXPORTADA
export const romanToInt = (roman) => {
  const romanNumerals = {
    'I': 1, 'II': 2, 'III': 3, 'IV': 4, 'V': 5,
    'VI': 6, 'VII': 7, 'VIII': 8, 'IX': 9, 'X': 10,
    'XI': 11, 'XII': 12, 'XIII': 13, 'XIV': 14, 'XV': 15
  };
  return romanNumerals[roman] || 999;
};

// Extraer número romano de cargo - EXPORTADA
export const extraerNumeroRomano = (cargo) => {
  if (!cargo) return '';
  const match = cargo.toUpperCase().match(/(VOCAL|SUPLENTE)\s+([IVX]+)/);
  return match ? match[2].trim() : '';
};

// Ordenar representantes por jerarquía
export const ordenarPorJerarquiaConVocales = (representantes) => {
  const ordenJerarquia = {
    'presidente': 1, 'vicepresidente': 2, 'secretario': 3, 'pro-secretario': 4,
    'tesorero': 5, 'vocal': 6, 'fiscal': 7, 'suplente': 8,
  };

  return [...representantes].sort((a, b) => {
    const cargoA = a.categorias?.find(cat => cat.Id === 4 && cat.Estado !== 'Inactivo')?.Cargo?.toLowerCase() || '';
    const cargoB = b.categorias?.find(cat => cat.Id === 4 && cat.Estado !== 'Inactivo')?.Cargo?.toLowerCase() || '';
    
    const obtenerTipoCargo = (cargo) => {
      const cargoLimpio = cargo.trim().toLowerCase();
      if (cargoLimpio === 'presidente') return 'presidente';
      if (cargoLimpio === 'vicepresidente') return 'vicepresidente';
      if (cargoLimpio === 'secretario') return 'secretario';
      if (cargoLimpio === 'pro-secretario') return 'pro-secretario';
      if (cargoLimpio === 'tesorero') return 'tesorero';
      if (cargoLimpio === 'fiscal') return 'fiscal';
      if (cargoLimpio.startsWith('vocal ')) return 'vocal';
      if (cargoLimpio.startsWith('suplente ')) return 'suplente';
      return 'otro';
    };

    const tipoCargoA = obtenerTipoCargo(cargoA);
    const tipoCargoB = obtenerTipoCargo(cargoB);
    const jerarquiaA = ordenJerarquia[tipoCargoA] || 99;
    const jerarquiaB = ordenJerarquia[tipoCargoB] || 99;
    
    if (jerarquiaA === jerarquiaB && (tipoCargoA === 'vocal' || tipoCargoA === 'suplente')) {
      const numeroA = romanToInt(extraerNumeroRomano(cargoA));
      const numeroB = romanToInt(extraerNumeroRomano(cargoB));
      return numeroA - numeroB;
    }
    
    return jerarquiaA - jerarquiaB;
  });
};

// Obtener cargo de junta directiva - EXPORTADA
export const obtenerCargoJunta = (representante) => {
  const categoriaJunta = representante.categorias?.find(cat => cat.Id === 4 && cat.Estado !== 'Inactivo');
  return categoriaJunta?.Cargo || 'MIEMBRO';
};

// ========================================
// FILTROS DE REPRESENTANTES CON INHABILITADO
// ========================================

/**
 * Filtrar representantes por asociación y categoría
 * ✅ INCLUYE FILTRO INHABILITADO
 */
export const getRepresentantesDeCategoria = (representantes, asociacionId, categoriaId) => {
  return representantes.filter(rep =>
    rep.cod_asociacion === asociacionId &&
    rep.categorias?.some(cat => cat.Id === categoriaId && cat.Estado !== 'Inactivo') &&
    rep.estatus !== 'INHABILITADO' // ← YA ESTABA - CORRECTO ✅
  );
};

/**
 * Obtener representantes gubernamentales
 * ✅ INCLUYE FILTRO INHABILITADO
 */
export const getRepresentantesGubernamentales = (representantes) => {
  return representantes.filter((rep) =>
    rep.categorias?.some((cat) => cat.Id === 5 && cat.Estado !== 'Inactivo') &&
    rep.estatus !== 'INHABILITADO' // ← FILTRO AGREGADO
  );
};

/**
 * Obtener categorías de una asociación
 * ✅ INCLUYE FILTRO INHABILITADO EN EL CONTEO
 */
export const getCategoriasDeAsociacion = (representantes, categorias, cod_asociacion) => {
  // Filtrar representantes de la asociación excluyendo inhabilitados y con al menos una categoría activa
  const representantesAsociacion = representantes.filter(
    (rep) => rep.cod_asociacion === cod_asociacion &&
    rep.categorias?.some(cat => cat.Estado !== 'Inactivo') &&
    rep.estatus !== 'INHABILITADO' // ← FILTRO AGREGADO
  );

  const categoriasIds = new Set();
  representantesAsociacion.forEach((rep) => {
    rep.categorias.forEach((cat) => {
      if (cat.Id !== 5 && cat.Estado !== 'Inactivo') categoriasIds.add(cat.Id);
    });
  });

  return Array.from(categoriasIds).map((id) => {
    const categoria = categorias.find((c) => c.Id === id);
    // Contar solo representantes activos (ya filtrados arriba) con esta categoría activa
    const count = representantesAsociacion.filter((rep) =>
      rep.categorias.some((cat) => cat.Id === id && cat.Estado !== 'Inactivo')
    ).length;
    
    // Personalizar el nombre de la categoría  
    let descripcionPersonalizada = categoria?.Descripcion;
    if (categoria?.Descripcion?.toUpperCase() === 'FCN') {
      descripcionPersonalizada = 'Representantes ante la Junta de FCN';
    }
    
    return { 
      ...categoria, 
      Descripcion: descripcionPersonalizada,
      count 
    };
  });
};